import { useCallback, useEffect, useRef, useState } from 'react';

const SEARCH_DELAY_MS = 400;

/**
 * Generic debounced people-search hook for Create Plan autocompletes.
 * Residents and editors share identical search/abort/debounce behavior and only
 * differ by the underlying service call, which is injected here.
 * Prefetches on mount (no query) so the dropdown has data on open.
 * @param {(query: string, signal: AbortSignal) => Promise<{ results?: object[] }>} searchFn
 * @returns {{ options: object[], isLoading: boolean, handleSearchChange: (query: string) => void }}
 */
export const useDebouncedPeopleSearch = (searchFn) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const clearPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const runSearch = useCallback(
    async (trimmed) => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await searchFn(trimmed, controller.signal);
        setOptions(response.results || []);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          setOptions([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [searchFn],
  );

  // Prefetch default results so opening the field shows a list immediately.
  useEffect(() => {
    setIsLoading(true);
    runSearch('');

    return () => clearPending();
  }, [clearPending, runSearch]);

  const handleSearchChange = useCallback(
    (query) => {
      clearPending();

      const trimmed = (query || '').trim();
      setIsLoading(true);
      debounceRef.current = setTimeout(() => runSearch(trimmed), SEARCH_DELAY_MS);
    },
    [clearPending, runSearch],
  );

  return {
    options,
    isLoading,
    handleSearchChange,
  };
};
